import { MultiSelect, MultiSelectProps } from '../multi-select';
import { ProviderImage } from '../provider-image';
import { GlobeAsiaAustraliaIcon } from '@heroicons/react/24/outline';
import { REGION_LIST } from './region';
import {
  IntegrationFilter,
  RegionFilter,
  RegionFilterControls,
} from './filter-types';

/**
 * Width of the region dropdown to properly show region codes without ellipses.
 */
const REGION_DROPDOWN_WIDTH = 400;

export const RegionFilterFC = ({
  label,
  value,
  onClose,
  filters,
  setValue,
  filteredValues,
  placement,
  mode,
  operator,
  showOperator,
  disabled,
  displayMaxItems,
  selectText,
  permanent,
  showInlineClear,
  hideIcon,
  wrapperClassName,
  dataTestId,
}: RegionFilter & RegionFilterControls) => {
  const regionListWithId = REGION_LIST.map((region, index) => ({
    ...region,
    id: index,
  }));
  const provider = filters.find((filter) => filter.type === 'provider');
  const providerValues =
    provider?.providerOptions?.map((item) => item.value) || [];
  const selectedProviderValues =
    provider && filteredValues ? (filteredValues[provider.id] as string[]) : [];
  const integration = filters.find(
    (filter) => filter.type === 'integrationId',
  ) as IntegrationFilter | undefined;
  const integrationProviderValues =
    integration && filteredValues
      ? (filteredValues[integration.id] as string[])?.map((x) => {
          return integration.data?.find((int) => int.integrationId === x)
            ?.provider;
        })
      : [];
  const options =
    regionListWithId
      .filter((type) => {
        const isEveryRegionValid =
          !selectedProviderValues?.length &&
          !integrationProviderValues?.length &&
          !providerValues?.length;

        const isSelectedProvider = selectedProviderValues?.includes(
          type.provider,
        );
        const isIntegrationProvider = integrationProviderValues?.includes(
          type.provider,
        );
        const isRegionInValue = value?.includes(`${type.key}${type.id}`);
        const isDefaultProvider =
          !selectedProviderValues?.length &&
          providerValues?.length &&
          providerValues?.includes(type.provider);

        return (
          isEveryRegionValid ||
          isSelectedProvider ||
          isIntegrationProvider ||
          isRegionInValue ||
          isDefaultProvider
        );
      })
      .map((region) => ({
        value: region.key,
        searchString: `${region.provider} ${region.label ?? ''} ${region.key}`,
        label: (
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-2">
              <ProviderImage
                sizeClassName="w-8 h-6 flex-shrink-0"
                provider={region.provider}
              />
              <div className="truncate">{region.label ?? region.key}</div>
            </div>
            <div className="text-darker-gray truncate text-xs">
              {region.key}
            </div>
          </div>
        ),
        selectedDisplay: (
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-2">
              <ProviderImage
                sizeClassName="w-8 h-6 flex-shrink-0"
                provider={region.provider}
              />
              <div className="truncate">{region.label ?? region.key}</div>
            </div>
            <div className="text-darker-gray truncate pl-2 text-xs">
              {region.key}
            </div>
          </div>
        ),
      })) || [];

  const multiSelectProps = {
    label,
    dataTestId,
    operator,
    showOperator,
    options,
    icon: GlobeAsiaAustraliaIcon,
    placement,
    onClose,
    disabled,
    displayMaxItems,
    selectText,
    permanent,
    showInlineClear,
    hideIcon,

    allowSearch: true,
    virtualize: true,
    displayButtonClassName: wrapperClassName,
    virtualizationOptions: {
      containerStyles: {
        maxWidth: REGION_DROPDOWN_WIDTH,
      },
      virtuosoStyle: {
        maxWidth: REGION_DROPDOWN_WIDTH,
        width: REGION_DROPDOWN_WIDTH,
      },
    },
  } satisfies Partial<MultiSelectProps<(typeof options)[0]>>;

  return mode === 'single' ? (
    <MultiSelect
      {...multiSelectProps}
      mode="single"
      value={value}
      setValue={setValue}
    />
  ) : (
    <MultiSelect
      {...multiSelectProps}
      mode="multiple"
      value={value}
      setValue={setValue}
      showSelectAll
    />
  );
};
